import React, { useState, useEffect } from 'react';
import { auth, db, storage } from '../firebaseConfig';
import { 
  updateProfile, 
  updatePassword, 
  reauthenticateWithCredential,
  EmailAuthProvider,
  deleteUser
} from 'firebase/auth';
import { 
  doc, 
  updateDoc, 
  getDoc,
  deleteDoc
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL 
} from 'firebase/storage';
import { showToast } from '../components/Toast';
import '../css/perfil.css';

function Profile() {
  const user = auth.currentUser;
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');

  // States for changing name
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [nameStatus, setNameStatus] = useState(null);

  // States for changing password
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordStatus, setPasswordStatus] = useState(null);

  // States for changing photo
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoStatus, setPhotoStatus] = useState(null);

  // Load user information
  useEffect(() => {
    const loadUserData = async () => {
      if (!user) return;
      try {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          setUserData(userDoc.data());
        }
        setNewName(user.displayName || '');
      } catch (error) {
        console.error('Error loading user data:', error);
      }
    };
    loadUserData();
  }, [user]);

  // ============= CHANGE NAME =============
  const handleUpdateName = async (e) => {
    e.preventDefault();
    if (!newName.trim()) {
      setNameStatus({ type: 'error', message: '⚠️ Name cannot be empty' });
      return;
    }

    setLoading(true);
    setNameStatus({ type: 'loading', message: '⏳ Updating name...' });

    try {
      // Actualizar en Firebase Auth
      await updateProfile(user, {
        displayName: newName,
      });

      // Actualizar en Firestore
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        displayName: newName,
      });

      setNameStatus({ type: 'success', message: '✅ Name updated successfully' });
      setEditingName(false);
      showToast('Name updated successfully', 'success');
      
      setTimeout(() => setNameStatus(null), 3000);
    } catch (error) {
      console.error('Error updating name:', error);
      setNameStatus({ type: 'error', message: `❌ Error: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  // ============= CHANGE PASSWORD =============
  const handleUpdatePassword = async (e) => {
    e.preventDefault();

    if (!currentPassword) {
      setPasswordStatus({ type: 'error', message: '⚠️ Please enter your current password' });
      return;
    }

    if (!newPassword) {
      setPasswordStatus({ type: 'error', message: '⚠️ Please enter a new password' });
      return;
    }

    if (newPassword.length < 6) {
      setPasswordStatus({ type: 'error', message: '⚠️ Password must be at least 6 characters' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordStatus({ type: 'error', message: '⚠️ Passwords do not match' });
      return;
    }

    if (newPassword === currentPassword) {
      setPasswordStatus({ type: 'error', message: '⚠️ New password must be different' });
      return;
    }

    setLoading(true);
    setPasswordStatus({ type: 'loading', message: '⏳ Updating password...' });

    try {
      // Reauthenticate the user
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, newPassword);

      setPasswordStatus({ type: 'success', message: '✅ Password updated successfully' });
      showToast('Password updated successfully', 'success');
      
      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordForm(false);
      
      setTimeout(() => setPasswordStatus(null), 3000);
    } catch (error) {
      console.error('Error updating password:', error);
      
      let errorMessage = '❌ Error updating password';
      if (error.code === 'auth/wrong-password') {
        errorMessage = '❌ Current password is incorrect';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = '❌ Password is too weak';
      }
      
      setPasswordStatus({ type: 'error', message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  // ============= CHANGE PROFILE PHOTO =============
  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setPhotoStatus({ type: 'error', message: '❌ Please select a valid image' });
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setPhotoStatus({ type: 'error', message: '❌ Image must not exceed 5MB' });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadPhoto = async () => {
    if (!photoPreview) {
      setPhotoStatus({ type: 'error', message: '❌ Please select an image' });
      return;
    }

    setUploadingPhoto(true);
    setPhotoStatus({ type: 'loading', message: '⏳ Uploading photo...' });

    try {
      // Convert Data URL to Blob
      const response = await fetch(photoPreview);
      const blob = await response.blob();

      // Create reference in Storage
      const photoRef = ref(storage, `profile-photos/${user.uid}/avatar`);

      // Upload file
      await uploadBytes(photoRef, blob);

      // Get download URL
      const downloadURL = await getDownloadURL(photoRef);
      const photoPath = `profile-photos/${user.uid}/avatar`;

      // Update in Firebase Auth
      await updateProfile(user, {
        photoURL: downloadURL,
      });

      // Update in Firestore - guardar URL E ruta
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        photoURL: downloadURL,
        photoPath: photoPath,
        photoUpdatedAt: new Date().toISOString(),
      });

      // Forzar recarga del usuario para asegurar que el token es fresco
      await user.reload();

      setPhotoStatus({ type: 'success', message: '✅ Photo updated successfully' });
      showToast('Profile photo updated successfully', 'success');
      setPhotoPreview(null);
      
      setTimeout(() => setPhotoStatus(null), 3000);
    } catch (error) {
      console.error('Error uploading photo:', error);
      setPhotoStatus({ type: 'error', message: `❌ Error: ${error.message}` });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleCancelPhotoUpload = () => {
    setPhotoPreview(null);
    setPhotoStatus(null);
  };

  // ============= DELETE ACCOUNT =============
  const handleDeleteAccount = async (e) => {
    e.preventDefault();

    if (!deletePassword) {
      setPasswordStatus({ type: 'error', message: '⚠️ Please enter your password to confirm' });
      return;
    }

    setLoading(true);
    setPasswordStatus({ type: 'loading', message: '⏳ Deleting account...' });

    try {
      // Reauthenticate the user
      const credential = EmailAuthProvider.credential(user.email, deletePassword);
      await reauthenticateWithCredential(user, credential);

      // Delete document in Firestore
      const userRef = doc(db, 'users', user.uid);
      await deleteDoc(userRef);

      // Delete user in Firebase Auth
      await deleteUser(user);

      showToast('Account deleted successfully', 'success');
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting account:', error);
      
      let errorMessage = '❌ Error deleting account';
      if (error.code === 'auth/wrong-password') {
        errorMessage = '❌ Password is incorrect';
      }
      
      setPasswordStatus({ type: 'error', message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="profile-page-wrapper">
        <div className="profile-container">
          <div className="loading">
            <h2>⚠️ Please log in to view your profile</h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page-wrapper">
      <div className="profile-container">
        {/* Profile Content */}
        <div className="profile-content">
          {/* Sidebar - Avatar y Info */}
          <div className="profile-sidebar">
            <div className="profile-avatar-card">
              <div className="avatar-display">
                {photoPreview ? (
                  <img src={photoPreview} alt="Preview" />
                ) : user.photoURL ? (
                  <img 
                    src={user.photoURL} 
                    alt={user.displayName || 'User'}
                    onError={async (e) => {
                      console.warn('Avatar URL failed, regenerating...');
                      try {
                        const userRef = doc(db, 'users', user.uid);
                        const userDoc = await getDoc(userRef);
                        if (userDoc.exists() && userDoc.data().photoPath) {
                          const newURL = await getDownloadURL(ref(storage, userDoc.data().photoPath));
                          e.target.src = newURL;
                          await updateProfile(user, { photoURL: newURL });
                        }
                      } catch (err) {
                        console.error('Regeneration failed:', err);
                        e.target.style.display = 'none';
                      }
                    }}
                  />
                ) : (
                  <div className="avatar-placeholder">
                    {user.displayName ? user.displayName.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>

              <div className="user-info-display">
                <p className="user-name-display">{user.displayName || 'User'}</p>
                <p className="user-email-display">{user.email}</p>
                {userData?.role && (
                  <p className="user-role-display">
                    {userData.role === 'superadmin' ? '👑 Super Admin' : userData.role === 'admin' ? '⭐ Admin' : '🎵 Client'}
                  </p>
                )}
              </div>

              {photoStatus && (
                <div className={`form-status ${photoStatus.type}`}>
                  <span className="status-icon">
                    {photoStatus.type === 'loading' ? '⏳' : photoStatus.type === 'success' ? '✅' : '❌'}
                  </span>
                  {photoStatus.message}
                </div>
              )}

              <div className="file-input-wrapper">
                <input
                  type="file"
                  id="photo-input"
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  disabled={uploadingPhoto}
                />
                <button
                  className="btn-change-photo"
                  onClick={() => document.getElementById('photo-input').click()}
                  disabled={uploadingPhoto}
                >
                  📷 {uploadingPhoto ? 'Uploading...' : photoPreview ? 'Change image' : 'Change photo'}
                </button>
              </div>

              {photoPreview && (
                <div className="form-actions" style={{ marginTop: '15px' }}>
                  <button
                    className="btn-submit"
                    onClick={handleUploadPhoto}
                    disabled={uploadingPhoto || loading}
                  >
                    ✅ Confirm
                  </button>
                  <button
                    className="btn-cancel"
                    onClick={handleCancelPhotoUpload}
                    disabled={uploadingPhoto}
                  >
                    ❌ Cancel
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Main Forms */}
          <div className="profile-forms">
            {/* Section: Change Name */}
            <div className="profile-section">
              <h2>✏️ Change Name</h2>

              {nameStatus && (
                <div className={`form-status ${nameStatus.type}`}>
                  <span className="status-icon">
                    {nameStatus.type === 'loading' ? '⏳' : nameStatus.type === 'success' ? '✅' : '❌'}
                  </span>
                  {nameStatus.message}
                </div>
              )}

              {editingName ? (
                <form onSubmit={handleUpdateName}>
                  <div className="form-group">
                    <label htmlFor="name">New Name</label>
                    <input
                      id="name"
                      type="text"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder="Enter your new name"
                      disabled={loading}
                    />
                  </div>

                  <div className="form-actions">
                    <button
                      type="submit"
                      className="btn-submit"
                      disabled={loading || !newName.trim()}
                    >
                      💾 Guardar
                    </button>
                    <button
                      type="button"
                      className="btn-cancel"
                      onClick={() => {
                        setEditingName(false);
                        setNewName(user.displayName || '');
                        setNameStatus(null);
                      }}
                      disabled={loading}
                    >
                      ❌ Cancelar
                    </button>
                  </div>
                </form>
              ) : (
                <div>
                  <p style={{ color: '#FFA500', marginBottom: '15px' }}>
                    Current name: <strong>{user.displayName || 'Not specified'}</strong>
                  </p>
                  <button
                    className="btn-submit"
                    onClick={() => setEditingName(true)}
                  >
                    ✏️ Edit Name
                  </button>
                </div>
              )}
            </div>

            {/* Section: Change Password */}
            <div className="profile-section">
              <h2>🔐 Change Password</h2>

              {passwordStatus && (
                <div className={`form-status ${passwordStatus.type}`}>
                  <span className="status-icon">
                    {passwordStatus.type === 'loading' ? '⏳' : passwordStatus.type === 'success' ? '✅' : '❌'}
                  </span>
                  {passwordStatus.message}
                </div>
              )}

              {showPasswordForm ? (
                <form onSubmit={handleUpdatePassword}>
                  <div className="form-group">
                    <label htmlFor="current-password">Current Password</label>
                    <input
                      id="current-password"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter your current password"
                      disabled={loading}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="new-password">New Password</label>
                    <input
                      id="new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter the new password"
                      disabled={loading}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="confirm-password">Confirm Password</label>
                    <input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm the new password"
                      disabled={loading}
                    />
                  </div>

                  <div className="form-actions">
                    <button
                      type="submit"
                      className="btn-submit"
                      disabled={loading || !currentPassword || !newPassword || !confirmPassword}
                    >
                      💾 Guardar
                    </button>
                    <button
                      type="button"
                      className="btn-cancel"
                      onClick={() => {
                        setShowPasswordForm(false);
                        setCurrentPassword('');
                        setNewPassword('');
                        setConfirmPassword('');
                        setPasswordStatus(null);
                      }}
                      disabled={loading}
                    >
                      ❌ Cancelar
                    </button>
                  </div>
                </form>
              ) : (
                <button
                  className="btn-submit"
                  onClick={() => setShowPasswordForm(true)}
                >
                  🔐 Change Password
                </button>
              )}
            </div>

            {/* Section: Danger - Delete Account */}
            <div className="profile-section" style={{ borderColor: '#FF4444' }}>
              <h2 style={{ color: '#FF6B6B' }}>⚠️ Danger Zone</h2>
              <p style={{ color: '#FFA500', marginBottom: '15px' }}>
                Deleting your account is a permanent and irreversible action. All your data will be deleted.
              </p>
              <button
                className="btn-delete-account"
                onClick={() => setShowDeleteModal(true)}
              >
                🗑️ Delete Account
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal for Confirming Account Deletion */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>⚠️ Confirm Account Deletion</h3>
            <p>This action cannot be undone. All your data will be permanently deleted.</p>

            <form onSubmit={handleDeleteAccount}>
              <div className="form-group">
                <label htmlFor="delete-password">Password to confirm</label>
                <input
                  id="delete-password"
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Enter your password"
                  disabled={loading}
                />
              </div>

              {passwordStatus && (
                <div className={`form-status ${passwordStatus.type}`}>
                  <span className="status-icon">
                    {passwordStatus.type === 'loading' ? '⏳' : passwordStatus.type === 'success' ? '✅' : '❌'}
                  </span>
                  {passwordStatus.message}
                </div>
              )}

              <div className="modal-actions">
                <button
                  type="submit"
                  className="btn-confirm"
                  disabled={loading || !deletePassword}
                >
                  🗑️ Delete Permanently
                </button>
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletePassword('');
                    setPasswordStatus(null);
                  }}
                  disabled={loading}
                >
                  ❌ Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;
